<template>
    <div class="transition-all ease-in-out lots-list fixed md:relative inset-0 ml-16 md:ml-0 z-50 flex-1 bg-white flex flex-col md:rounded-lg">
        <div class="flex p-4">
            <div class="flex flex-1 justify-start items-center">
                <Paddle v-if="user && user.paddle" :paddle="user.paddle" bg-color="#000000" text-color="#FFFFFF" width="35px" />
                <div v-if="user && user.id" class="flex flex-col">
                    <div class="pl-2">{{ user.first_name }} {{ user.last_name }}</div>
                    <div class="pl-2 text-xs">{{ $t('account.your_paddle') }} {{ user.paddle }}</div>
                    <div v-if="user.approval_status == 1" class="pl-2 text-xs">{{ $t('account.status_asteptare') }}</div>
                    <div v-else-if="user.approval_status == 2" class="pl-2 text-xs">{{ $t('account.aprobat') }}</div>
                    <div v-else class="pl-2 text-xs">{{ $t('account.neaprobat') }}</div>
                </div>
                <div v-else class="text-sm text-gray-500 hover:text-black">
                    <a :href="`/${locale}/login`">{{ $t('account.login') }}</a>
                </div>
            </div>
            <div class="lang-switcher flex flex-1 justify-end items-start space-x-2">
                <a :href="`/ro/live/${auction.id}`">
                    <img src="@live/assets/images/ro.png">
                </a>
                <a :href="`/en/live/${auction.id}`">
                    <img src="@live/assets/images/en.png">
                </a>
                <a :href="`/hr/live/${auction.id}`">
                    <img src="@live/assets/images/hr.png">
                </a>
                <a :href="`/bg/live/${auction.id}`">
                    <img src="@live/assets/images/bg.png">
                </a>
            </div>
        </div>
        <div v-if="auction" class="auction p-4 text-xs">
            {{ auction.n }}
        </div>

        <div class="lots flex flex-col flex-1" v-if="filteredLots">
            <div class="toolbar flex justify-between px-4 py-4 border-gray-300 border-t border-b">
                <div class="flex flex-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input class="flex-1 px-2" v-model="filter" :placeholder="$t('lots.search_lots')" size="is-small" type="text">
                    <button @click="filter=''" :class="{'opacity-0': filter == ''}" class="text-gray-500 hover:text-black mr-4">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="flex ml-2">
                    <button :disabled="show == 'previous'" class="text-xs text-gray-500 uppercase mr-2 disabled:text-gray-900" @click="toggleShow">{{ $t('lots.prev') }}</button>
                    <button :disabled="show == 'upcoming'" class="text-xs text-gray-500 uppercase disabled:text-gray-900" @click="toggleShow">{{ $t('lots.next') }}</button>
                </div>
            </div>
            <div v-if="filteredLots.length == 0" class="flex flex-1 items-center justify-center">
                <div v-if="filter">
                    {{ $t('lots.no_results') }} <i>{{ filter }}</i>
                </div>
                <div v-else>
                    {{ $t('lots.no_more_lots') }}
                </div>
            </div>
            <div v-if="filteredLots.length > 0" class="flex flex-col flex-1 bg-gray-50 overflow-x-none overflow-y-scroll" style="flex: 1 1 auto; height: 10px;">
                <div :id="`lot-id-${lot.id}`" @click="selectLot(lot)" :key="lot.id" class="grid grid-cols-12 gap-4 cursor-pointer text-xs" :class="isCurrentLotClass(lot)" v-for="lot in filteredLots">
                    <div class="lot-listing-image col-span-2 p-2 flex items-center" style="min-height: 60px;">
                        <ListingImageItem :key="lot.id" :source="lot.img80" />
                    </div>
                    <div v-if="lot" class="col-span-6 flex flex-1 flex-col py-4 justify-center">
                        <div class="uppercase">
                            {{ $t('general.lot') }} {{ lot.no }}
                        </div>
                        <div>
                            <span v-if="lot.a">{{ lot.a }},&nbsp;</span>{{ lot.t }}
                        </div>
                    </div>
                    <div class="col-span-4 py-4 pr-4 flex items-start justify-end text-right">
                        <div v-if="lot.s == 0" class="flex flex-col"><!-- pending -->
                            <div>
                                {{ $t('lot.next') }}
                            </div>
                            <div v-if="lot.emin" class="flex flex-no-wrap">
                                <span class="mr-1">{{ $t('general.est') }}:</span>
                                <money-format
                                    :value="lot.emin"
                                    locale="ro"
                                    currency-code="eur"
                                    :subunits-value="false"
                                    :hide-subunits="true">
                                </money-format>
                                <span class="mx-1">&ndash;</span>
                                <money-format
                                    :value="lot.emax"
                                    locale="ro"
                                    currency-code="eur"
                                    :subunits-value="false"
                                    :hide-subunits="true">
                                </money-format>
                            </div>
                            <div v-else class="flex items-center">
                                <span class="mr-1">{{ $t('general.sp') }}:</span>
                                <money-format
                                    :value="lot.pp"
                                    locale="ro"
                                    currency-code="eur"
                                    :subunits-value="false"
                                    :hide-subunits="true">
                                </money-format>
                            </div>
                        </div>
                        <div v-if="lot.s == 1" class="flex flex-col"><!-- sold -->
                            <div>
                                {{ $t('lots.sold') }}
                            </div>
                            <div>
                                <money-format
                                    :value="lot.sp"
                                    locale="ro"
                                    currency-code="eur"
                                    :subunits-value="false"
                                    :hide-subunits="true">
                                </money-format>
                            </div>
                        </div>
                        <div v-if="lot.s == 2" class="flex flex-col"><!-- pass -->
                            {{ $t('lots.pass') }}
                        </div>
                        <div v-if="lot.s == 3" class="flex flex-col"><!-- withdrawn -->
                            {{ $t('lots.withdrawn') }}
                        </div>
                        <div v-if="lot.s == 4" class="flex flex-col"><!-- withdrawn -->
                            {{ $t('lots.withdrawn') }}
                        </div>
                        <div v-if="lot.s == 5" class="flex flex-col"><!-- post -->
                            {{ $t('lots.sold_post') }}
                        </div>
                        <div v-if="lot.s == 6" class="flex flex-col"><!-- withdrawn -->
                            {{ $t('lots.withdrawn') }}
                        </div>
                        <div v-if="lot.s == 7" class="flex flex-col"><!-- open -->
                            <div>
                                {{ $t('lots.open') }}
                            </div>
                            <div v-if="lot.p > 0">
                                <money-format
                                    :value="lot.p"
                                    locale="ro"
                                    currency-code="eur"
                                    :subunits-value="false"
                                    :hide-subunits="true">
                                </money-format>
                            </div>
                        </div>
                        <div v-if="lot.s == 8" class="flex flex-col"><!-- recalled -->
                            {{ $t('lots.recalled') }}
                        </div>
                        <div v-if="lot.s == 31" class="flex flex-col"><!-- withdrawn -->
                            {{ $t('lots.withdrawn') }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="p-4 text-xs flex space-x-1 items-center justify-center border-gray-300 border-t">
                <span v-if="$t('general.contact_1') != ''">{{ $t('lots.support') }} <span v-html="$t('general.contact_1')"></span></span>
                <template v-if="$t('general.contact_2') != ''">
                    <span>/<span v-html="$t('general.contact_2')"></span></span>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import Fuse from 'fuse.js'
import { mapGetters } from 'vuex'
import ListingImageItem from '../global/ListingImageItem'
import Paddle from '../global/Paddle'

export default {
    components: {
        ListingImageItem, Paddle
    },
    data () {
        return {
            filter: '',
            show: 'upcoming',
            langs: ['ro', 'en', 'bg', 'hr']
        }
    },
    computed: {
        ...mapGetters({
            user: 'getUser',
            auction: 'getAuction',
            locale: 'getLocale',
            auctionCurrentLot: 'getLot',
            selectedLot: 'getSelectedLot',
            lots: 'getLots',
            showLots: 'getShowLots',
            recallTimer: 'getRecallTimer'
        }),
        filteredLots() {
            if(! this.filter || this.filter == '') {
                let show = this.show
                return this.lots.filter(lot => {
                    if (! this.auctionCurrentLot) return true
                    if (! this.auctionCurrentLot.no) return true
                    if (show == 'upcoming') {
                        return lot.no >= this.auctionCurrentLot.no
                    } else {
                        return lot.no < this.auctionCurrentLot.no
                    }
                })
            } else {
                this.$store.commit('SET_SELECTED_LOT', null)
                if(isNaN(this.filter)) {
                    const fuse = new Fuse(this.lots, {
                        includeScore: true,
                        keys: ['t', 'a', 'no']
                    })
                    let results = fuse.search(this.filter)
                    return Array.from(results, el => el.item)
                } else {
                    return this.lots.filter(lot => lot.no == this.filter)
                }
            }
        },
        currentLot () {
            if (this.selectedLot) {
                return this.selectedLot
            } else if (this.auctionCurrentLot) {
                return this.auctionCurrentLot
            } else {
                return null
            }
        },
        isAuctionLot () {
            if(! this.selectedLot) return true
            if(! this.auctionCurrentLot) return true
            if(this.selectedLot.id == this.auctionCurrentLot.id) return true
            return false
        },
        hasPrev () {
            if(this.filteredLots.length != this.lots.length) return false
            return this.lots.findIndex(lot => {
                if(! this.currentLot) return false
                return this.currentLot.id == lot.id
            }) > 0
        },
        hasNext () {
            if(this.filteredLots.length != this.lots.length) return false
            return this.lots.findIndex(lot => {
                if(! this.currentLot) return false
                return this.currentLot.id == lot.id
            }) < this.lots.length - 1
        },
        lastLot () {
            return Math.max.apply(Math, this.lots.map(lot => lot.no))
        },
        isRecallWindowActive() {
            return !!this.auction
                && [4, 5].includes(this.auction.md)
                && this.auction.s == 3
                && this.recallTimer > 0
        }
    },
    methods: {
        selectLot (lot) {
            if(lot.id != this.auctionCurrentLot.id) {
                this.$store.commit('SET_SELECTED_LOT', lot.id)
            } else {
                this.$store.commit('SET_SELECTED_LOT', null)
            }
        },
        backToCurrentAuctionLot () {
            this.$store.commit('SET_SELECTED_LOT', null)
            if(! this.auctionCurrentLot) return
            this.$nextTick(() => {
                let el = document.getElementById(`lot-id-${this.auctionCurrentLot.id}`)
                if(el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
                }
            });
        },
        isCurrentLotClass (lot) {
            if (this.currentLot && lot.id == this.currentLot.id) {
                return 'bg-gray-900 text-white'
            } else {
                return ''
            }
        },
        toggleShow () {
            if (this.show == 'upcoming') {
                this.show = 'previous'
                this.$forceUpdate()
            } else {
                this.show = 'upcoming'
                this.$forceUpdate()
            }
        },
    },
    watch: {
        auctionCurrentLot(lot) {
            if(this.selectedLot) return
            if(! this.auctionCurrentLot) return
            this.$nextTick(() => {
                let el = document.getElementById(`lot-id-${this.auctionCurrentLot.id}`)
                if(el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
                }
            });
        },
        isRecallWindowActive(active) {
            if (active) {
                this.show = 'previous'
            }
        }
    },
    mounted() {
        if (this.isRecallWindowActive) {
            this.show = 'previous'
        }
    }
}
</script>
